import {
	apiClient,
	Button,
	ButtonGroup,
	FieldValidationError,
	TextFormInput,
	useLanguageResource,
} from "@ruter-as/web-components-and-tools";
import type React from "react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import widgetApi from "../../api/widgetApi";
import type Phone from "../../types/Phone";
import "./VerificationForm.scss";
import orderFormLanguageResource from "./lang-resource";

interface VerificationFormData {
	verificationCode: string;
}

interface Props {
	phone: Phone;
	phoneValidated: (verificationCode: string) => void;
	token: string;
	active: boolean;
}

const VerificationForm: React.FC<Props> = ({
	phone,
	phoneValidated,
	token,
	active,
}) => {
	const lang = useLanguageResource(orderFormLanguageResource);
	const verifactionFormMethods = useForm<VerificationFormData>();
	const [wrongVerificationCode, setWrongVerificationCode] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submittingCode, setSubmittingCode] = useState<boolean | undefined>();

	const code = verifactionFormMethods.watch();

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally clears the error when the user changes the verification code input
	useEffect(() => {
		setWrongVerificationCode(false);
	}, [code.verificationCode]);

	useEffect(() => {
		document.getElementById("verificationCode")?.focus();
	}, []);

	const submitVerificationCode = async (
		data: VerificationFormData,
	): Promise<void> => {
		if (phone) {
			setSubmitting(true);
			const response = await apiClient.request(
				widgetApi.auth.verifyPhoneCode(token, {
					phone: phone.nationalNumber,
					phoneCountryCode: phone.phoneCountryCode,
					phoneVerificationCode: data.verificationCode,
				}),
			);

			if (response.type === "success") {
				await phoneValidated(data.verificationCode);
			} else if (
				response.type === "HttpError" &&
				response.responseStatus === 403
			) {
				setWrongVerificationCode(true);
			} else {
				setWrongVerificationCode(() => {
					throw response.error;
				});
			}
			setSubmitting(false);
		}
	};

	const resendCode = async (): Promise<void> => {
		setSubmittingCode(true);
		const response = await apiClient.post("/api/auth/widget/verify-phone", {
			token,
			phone: phone.nationalNumber,
			phoneCountryCode: phone.phoneCountryCode,
		});
		if (response.type !== "success") {
			setSubmittingCode(() => {
				throw response.error;
			});
		}

		setSubmittingCode(undefined);
	};

	return (
		<FormProvider {...verifactionFormMethods}>
			<form
				onSubmit={verifactionFormMethods.handleSubmit(submitVerificationCode)}
				data-test-id="verification-form"
				className="verification-form"
			>
				<TextFormInput
					name="verificationCode"
					required
					maxLength={6}
					minLength={6}
					label={lang.inputs.verificationCode}
					disabled={!active}
				/>
				{wrongVerificationCode && (
					<FieldValidationError text={lang.errors.wrongVerificationCode} />
				)}
				{active && (
					<>
						<ButtonGroup>
							<Button type="submit" skin="primary" loading={submitting}>
								{lang.buttons.submitVerifationCode}
							</Button>
							<Button
								type="button"
								skin="secondary"
								loading={submittingCode}
								data-test-id="resend-code-button"
								onClick={resendCode}
							>
								{lang.buttons.resendCode}
							</Button>
						</ButtonGroup>

						<div className="link-buttons">
							<button
								data-test-id="reset-button"
								type="button"
								onClick={() => {
									window.location.reload();
								}}
							>
								{lang.buttons.reset}
							</button>
						</div>
					</>
				)}
			</form>
		</FormProvider>
	);
};

export default VerificationForm;
